import { StatusId, OperationMS, MsFormulaire } from '../types';

// Derivations PURES de l'onglet Consultation acheteur-MS : editabilite, validation,
// corps de sauvegarde + payload de lancement, mapping des erreurs 422. Gating par id.

// Limites de saisie des champs texte (guard front + compteur de caracteres), alignees sur la
// validation back correspondante -> pas de 422 surprise. Barreme : ~255 car. = 1 phrase.
export const CHAMP_PHRASE_MAX = 255;         // Acheteur & Operation : 1 phrase
export const CHAMP_PARTICULARITES_MAX = 600; // Particularites administratives : 2-3 phrases
export const CHAMP_TECHNIQUE_MAX = 1500;     // Descriptif technique : 5-6 phrases
export const CHAMP_CAHIER_MAX = 30000;       // Cahier des charges (besoin) : ~5-10 pages (3000 car./page)

// Editable uniquement en brouillon (statut 1).
export const isConsultationEditable = (operation: OperationMS): boolean =>
  operation.status?.id === StatusId.Brouillon;

// Mapping champ API (snake_case) -> champ formulaire (camelCase) pour remapper les 422.
export const API_TO_FORM_FIELD_MS: Record<string, string> = {
  nom: 'nomOperation',
  nom_ouvrage: 'nomOuvrage',
  reference: 'referenceInterne',
  besoin: 'besoin',
  adresse: 'adresse',
  code_postal: 'codePostal',
  ville: 'ville',
  coordonnees_maitrise: 'coordonneesMaitrise',
  coordonnees_maitrise_ouvrage: 'coordonneesMaitriseDOuvrage',
  conditionVisite: 'conditionVisite',
  // Descriptif administratif (refonte Consultation MS).
  pays: 'pays',
  moe_interlocuteur: 'moeInterlocuteur',
  moe_mail: 'moeMail',
  moe_telephone: 'moeTelephone',
  phases: 'phases',
  signataire_commande: 'signataire',
  modalite_facturation: 'modaliteFacturation',
  niveau_sensibilite: 'niveauSensibilite',
  visite_obligatoire: 'visiteObligatoire',
  verification_identites: 'verificationIdentites',
  habilitation: 'habilitation',
  charte_reglementation: 'charteReglementation',
  clause_suivi_environnemental: 'clauseSuiviEnvironnemental',
  // Descriptif technique.
  url_doc_technique: 'urlDocTechnique',
  modalite_acces_documentation: 'modaliteAccesDocumentation',
  liste_pieces_documentation: 'listePiecesDocumentation',
  // Descriptif procedural.
  date_limite_reception_offres: 'dateLimiteReceptionOffres',
  date_ouverture_plis: 'dateOuverturePlis',
  date_notification: 'dateNotification',
  budget_maximum: 'budgetMaximum',
  origine_financement: 'origineFinancement',
};

export const mapApiErrorsToForm = (errors: Record<string, string[]>): Record<string, string> => {
  const mapped: Record<string, string> = {};
  Object.keys(errors).forEach((field) => {
    const messages = errors[field];
    if (Array.isArray(messages) && messages.length > 0) {
      mapped[API_TO_FORM_FIELD_MS[field] || field] = messages[0];
    }
  });
  return mapped;
};

// --- Validateurs de format (purs) ---------------------------------------------
// Reutilises par la validation de sauvegarde ET par le feedback live du formulaire.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (value: string): boolean => EMAIL_RE.test(value.trim());

// Telephone francais : exactement 10 chiffres (0 suivi d'un chiffre 1-9 puis 8 chiffres).
// La saisie est limitee aux chiffres en amont ; on reste defensif en ignorant tout non-chiffre.
export const isValidPhoneFr = (value: string): boolean => /^0[1-9]\d{8}$/.test(value.replace(/\D/g, ''));

// Code postal francais : exactement 5 chiffres.
export const isValidCodePostalFr = (value: string): boolean => /^\d{5}$/.test(value.trim());

// URL : doit commencer par http:// ou https:// suivi d'un hote (sans espace). Accepte les liens
// sans TLD du type "https://france-transfert-567890", conformes a l'exemple de la spec.
export const isValidUrl = (value: string): boolean => /^https?:\/\/\S+$/i.test(value.trim());

// Messages d'erreur de format (partages formulaire <-> validation de sauvegarde).
export const FORMAT_ERROR_MS = {
  moeMail: "L'adresse mail n'est pas valide (ex : jean.dupont@exemple.fr)",
  moeTelephone: 'Le téléphone doit être un numéro français à 10 chiffres (ex : 06 78 90 12 34)',
  codePostal: 'Le code postal doit comporter 5 chiffres',
  urlDocTechnique: "Ce champ doit être une URL valide (commençant par http:// ou https://)",
  contactMail: "L'adresse mail n'est pas valide (ex : jean.dupont@exemple.fr)",
  contactTelephone: 'Le téléphone doit être un numéro français à 10 chiffres (ex : 06 78 90 12 34)',
} as const;

// --- Validation (champs requis MS, dont l'attestation BPU) ---------------------
export interface ConsultationValidation {
  valid: boolean;
  errors: Record<string, string>;
}

// Validation FORMAT uniquement (sans les champs requis). Utilisee par "Enregistrer et quitter" :
// on n'impose pas de tout remplir, mais on refuse un mail / telephone / code postal mal forme.
export const validateConsultationFormat = (formulaire?: MsFormulaire): ConsultationValidation => {
  const f = formulaire || {};
  const errors: Record<string, string> = {};
  if (f.codePostal?.trim() && !isValidCodePostalFr(f.codePostal)) errors.codePostal = FORMAT_ERROR_MS.codePostal;
  if (f.moeMail?.trim() && !isValidEmail(f.moeMail)) errors.moeMail = FORMAT_ERROR_MS.moeMail;
  if (f.moeTelephone?.trim() && !isValidPhoneFr(f.moeTelephone)) errors.moeTelephone = FORMAT_ERROR_MS.moeTelephone;
  // Descriptif technique : l'URL de doc, si renseignee, doit etre une vraie URL.
  if (f.urlDocTechnique?.trim() && !isValidUrl(f.urlDocTechnique)) errors.urlDocTechnique = FORMAT_ERROR_MS.urlDocTechnique;
  // Contact operationnel : mail et telephone sont FACULTATIFS, mais errones ils partent sur un
  // document contractuel et ne se decouvrent qu'au moment ou quelqu'un essaie de s'en servir.
  if (f.contactMail?.trim() && !isValidEmail(f.contactMail)) errors.contactMail = FORMAT_ERROR_MS.contactMail;
  if (f.contactTelephone?.trim() && !isValidPhoneFr(f.contactTelephone)) {
    errors.contactTelephone = FORMAT_ERROR_MS.contactTelephone;
  }
  return { valid: Object.keys(errors).length === 0, errors };
};

export const validateConsultationForm = (formulaire?: MsFormulaire): ConsultationValidation => {
  const f = formulaire || {};
  // Source unique du format : on part des erreurs de format, puis on ajoute les champs requis.
  const errors: Record<string, string> = { ...validateConsultationFormat(f).errors };

  if (!f.nomOuvrage?.trim()) errors.nomOuvrage = "Le nom de l'ouvrage est obligatoire";
  // Seul le NOM du contact est obligatoire : c'est l'interlocuteur que le titulaire devra joindre.
  // Fonction / mail / telephone restent facultatifs (formats verifies ci-dessus s'ils sont remplis).
  if (!f.contactNom?.trim()) errors.contactNom = 'Le nom du contact opérationnel est obligatoire';
  if (!f.nomOperation?.trim()) errors.nomOperation = "Le nom de l'opération est obligatoire";
  if (!f.attestationBPU) {
    errors.attestationBPU = 'Vous devez attester que les BPU ne correspondent pas à votre besoin';
  }
  // Adresse : elle ne vaut que si elle a ete choisie dans les suggestions IGN, ce que prouve la
  // presence des champs DERIVES (code postal, ville, pays). Ils ne sont plus affiches : leur erreur
  // doit donc remonter sur le champ Adresse, seul visible — sinon le lancement echoue sur un champ
  // que personne ne peut voir ni corriger.
  if (!f.adresse?.trim()) {
    errors.adresse = "L'adresse est obligatoire";
  } else if (!f.codePostal?.trim() || !f.ville?.trim() || !f.pays?.trim()) {
    errors.adresse = "Sélectionnez l'adresse dans la liste de suggestions pour valider la localisation";
  }
  if (!f.besoin?.trim()) errors.besoin = 'Le cahier des charges est obligatoire';
  if (f.visitePreavis && !f.conditionVisite?.trim()) {
    errors.conditionVisite = 'Les conditions de visite sont obligatoires';
  }
  // Descriptif administratif (refonte xlsx) — champs EDITABLES requis (le recap Acheteur, read-only,
  // n'est jamais valide ici). Ces champs ne persistent pas tant que le backend n'a pas ses colonnes.
  // typeCommande : derive (read-only), pas valide ici.
  // Le format du contact maitrise d'oeuvre (mail / telephone) est deja couvert par le seed
  // validateConsultationFormat ci-dessus (champs optionnels, stricts si renseignes).
  if (!f.niveauSensibilite?.trim()) errors.niveauSensibilite = 'Le niveau de sensibilité est obligatoire';
  if (!f.visiteObligatoire?.trim()) errors.visiteObligatoire = 'Le champ « Visite préalable obligatoire » est requis';
  if (!f.verificationIdentites?.trim()) errors.verificationIdentites = 'La vérification préalable des identités est obligatoire';
  if (!f.habilitation?.trim()) errors.habilitation = "L'habilitation particulière est obligatoire";
  // charteReglementation + clauseSuiviEnvironnemental : NON obligatoires (aucune validation requise).

  // Descriptif procedural + dates d'operation : champs marques * dans l'UI, requis AVANT le lancement.
  // dateDebutOperation / delaisFinOperation sont auto-remplis (defaut) : le garde-fou ne bloque que le
  // cas vide. dateLimiteReceptionOffres / dateOuverturePlis / dateNotification etaient bypassables (le
  // lancement passait champs vides) -> desormais bloquants.
  if (!f.dateDebutOperation) errors.dateDebutOperation = 'La date prévisionnelle de démarrage est obligatoire';
  if (f.delaisFinOperation === undefined || f.delaisFinOperation === null || f.delaisFinOperation === '') {
    errors.delaisFinOperation = 'La durée prévisionnelle est obligatoire';
  }
  if (!f.dateLimiteReceptionOffres?.trim()) {
    errors.dateLimiteReceptionOffres = 'La date et heure limite de réception des offres est obligatoire';
  }
  if (!f.dateOuverturePlis?.trim()) errors.dateOuverturePlis = "La date d'ouverture des plis est obligatoire";
  if (!f.dateNotification?.trim()) errors.dateNotification = 'La date de notification est obligatoire';

  return { valid: Object.keys(errors).length === 0, errors };
};

// --- Dates + corps de sauvegarde ----------------------------------------------
const formatDateForApi = (dateStr?: string | null): string | null => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().split('T')[0];
};

const addDays = (base: Date, days: number): Date => {
  const d = new Date(base);
  d.setDate(base.getDate() + days);
  return d;
};

// Helpers de dates derivees du formulaire MS (source unique, consommee par le FormulaireAdmin).
// ISO 'YYYY-MM-DD' en UTC (identique a l'ancien toIso du composant).
const isoDate = (d: Date): string => d.toISOString().split('T')[0];

// Fin de consultation = debut de consultation (ou aujourd'hui) + duree de consultation.
export const computeFinConsultation = (
  dateDebutConsultation: string | undefined,
  delaisConsultation: number,
): string =>
  isoDate(addDays(dateDebutConsultation ? new Date(dateDebutConsultation) : new Date(), delaisConsultation));

// Debut d'operation = aujourd'hui + duree de consultation.
export const computeDateDebutOperation = (delaisConsultation: number): string =>
  isoDate(addDays(new Date(), delaisConsultation));

// Fin d'operation = debut d'operation + duree d'execution.
export const computeDateFinOperation = (dateDebutOperation: string, delaisFin: number): string =>
  isoDate(addDays(new Date(dateDebutOperation), delaisFin));

// Dates d'operation : reprend la valeur du formulaire, sinon fallback (debut = aujourd'hui +
// duree de consultation ; fin = debut + duree d'execution).
const resolveDates = (f: MsFormulaire): { debut: string | null; fin: string | null } => {
  const delaisConsultation = Number(f.delaisConsultation) || 30;
  const delaisFin = Number(f.delaisFinOperation) || 30;

  const debutSource = f.dateDebutOperation || addDays(new Date(), delaisConsultation).toISOString().split('T')[0];
  const debut = formatDateForApi(debutSource);

  const finBase = debut ? new Date(debut) : new Date();
  const finSource = f.dateFinOperation || addDays(finBase, delaisFin).toISOString().split('T')[0];
  const fin = formatDateForApi(finSource);

  return { debut, fin };
};

// Corps de sauvegarde du descriptif (PUT /subsequents/operation/:id). On part de la vue-modele
// FUSIONNEE (formulaire ?? colonnes) pour ne PAS ecraser le descriptif quand le buffer d'edition
// est vide (op sauvegardee/non-brouillon).
export const buildSaveBody = (operation: OperationMS): Record<string, unknown> => {
  const f = buildConsultationFormData(operation);
  const { debut, fin } = resolveDates(f);
  return {
    nom: f.nomOperation?.trim() || '',
    nom_ouvrage: f.nomOuvrage?.trim() || '',
    // Contact operationnel de l'operation : n'ecrit NI sur le compte acheteur NI sur l'organisme.
    contact_nom: f.contactNom?.trim() || null,
    contact_fonction: f.contactFonction?.trim() || null,
    contact_mail: f.contactMail?.trim() || null,
    contact_telephone: f.contactTelephone?.trim() || null,
    reference: f.referenceInterne?.trim() || '',
    besoin: f.besoin?.trim() || '',
    delais: f.delaisConsultation?.toString() || '30',
    visite: f.visitePreavis ? '1' : '0',
    adresse: f.adresse?.trim() || '',
    code_postal: f.codePostal?.trim() || '',
    ville: f.ville?.trim() || '',
    coordonnees_maitrise: f.coordonneesMaitrise?.trim() || '',
    coordonnees_maitrise_ouvrage: f.coordonneesMaitriseDOuvrage?.trim() || '',
    conditionVisite: f.conditionVisite?.trim() || '',
    date_debut_operation: debut,
    date_fin_operation: fin,
    delais_fin_operation: f.delaisFinOperation?.toString() || '',
    // Descriptif administratif (refonte Consultation MS) — champs editables.
    pays: f.pays?.trim() || '',
    moe_interlocuteur: f.moeInterlocuteur?.trim() || '',
    moe_mail: f.moeMail?.trim() || '',
    moe_telephone: f.moeTelephone?.trim() || '',
    phases: f.phases?.trim() || '',
    signataire_commande: f.signataire?.trim() || '',
    modalite_facturation: f.modaliteFacturation?.trim() || '',
    niveau_sensibilite: f.niveauSensibilite?.trim() || '',
    visite_obligatoire: f.visiteObligatoire?.trim() || '',
    verification_identites: f.verificationIdentites?.trim() || '',
    habilitation: f.habilitation?.trim() || '',
    charte_reglementation: f.charteReglementation?.trim() || '',
    clause_suivi_environnemental: f.clauseSuiviEnvironnemental?.trim() || '',
    // Descriptif technique (champs documentaires, optionnels).
    url_doc_technique: f.urlDocTechnique?.trim() || '',
    modalite_acces_documentation: f.modaliteAccesDocumentation?.trim() || '',
    liste_pieces_documentation: f.listePiecesDocumentation?.trim() || '',
    // Descriptif procedural (champs saisis : 3 dates datetime-local + budget + origine financement).
    date_limite_reception_offres: f.dateLimiteReceptionOffres?.trim() || '',
    date_ouverture_plis: f.dateOuverturePlis?.trim() || '',
    date_notification: f.dateNotification?.trim() || '',
    budget_maximum: f.budgetMaximum?.trim() || '',
    origine_financement: f.origineFinancement?.trim() || '',
  };
};

// Payload de lancement (PUT /subsequents/:id/consultation) : descriptif + ids de lot.
export const buildLancementPayload = (operation: OperationMS): Record<string, unknown> => ({
  ...buildSaveBody(operation),
  id_lot_departement: operation.id_lot_departement,
  id_lot_technique: operation.id_lot_technique,
});

const computeDelaisFin = (operation: OperationMS): number => {
  const f = operation.formulaire || {};
  if (f.delaisFinOperation) return Number(f.delaisFinOperation);
  if (operation.delais_fin_operation) return Number(operation.delais_fin_operation);
  if (operation.date_debut_operation && operation.date_fin_operation) {
    const debut = new Date(operation.date_debut_operation);
    const fin = new Date(operation.date_fin_operation);
    if (!Number.isNaN(debut.getTime()) && !Number.isNaN(fin.getTime())) {
      return Math.max(10, Math.ceil(Math.abs(fin.getTime() - debut.getTime()) / (1000 * 60 * 60 * 24)));
    }
  }
  return 30;
};

// Vue-modele FUSIONNEE du formulaire : le buffer d'edition (operation.formulaire) prime, sinon
// les colonnes top-level de l'operation (descriptif sauvegarde). Permet l'affichage en lecture
// seule ET une sauvegarde non destructive (pas d'ecrasement si le buffer est vide).
export const buildConsultationFormData = (operation: OperationMS): MsFormulaire => {
  const f = operation.formulaire || {};
  return {
    ...f,
    nomOuvrage: f.nomOuvrage ?? operation.nom_ouvrage ?? '',
    // Contact operationnel : saisie de l'operation, sinon repli sur l'acheteur qui l'a initiee.
    // Le repli vit ICI (socle du formulaire) et non a l'affichage : `buildSaveBody` lit cette meme
    // vue, donc le contact pre-rempli est PERSISTE des le premier enregistrement.
    contactNom:
      f.contactNom ??
      (operation.contact_nom ||
        [operation.acheteur?.prenom, operation.acheteur?.nom].filter(Boolean).join(' ')),
    contactFonction: f.contactFonction ?? (operation.contact_fonction || operation.acheteur?.fonction || ''),
    contactMail: f.contactMail ?? (operation.contact_mail || operation.acheteur?.email_contact || ''),
    contactTelephone: f.contactTelephone ?? (operation.contact_telephone || operation.acheteur?.telephone || ''),
    nomOperation: f.nomOperation ?? operation.nom ?? '',
    referenceInterne: f.referenceInterne ?? operation.reference ?? '',
    besoin: f.besoin ?? operation.besoin ?? '',
    delaisConsultation: f.delaisConsultation ?? operation.delais ?? 10,
    delaisFinOperation: computeDelaisFin(operation),
    visitePreavis: f.visitePreavis ?? operation.visite ?? false,
    conditionVisite: f.conditionVisite ?? operation.conditionVisite ?? '',
    adresse: f.adresse ?? operation.adresse ?? '',
    codePostal: f.codePostal ?? operation.code_postal ?? '',
    ville: f.ville ?? operation.ville ?? '',
    coordonneesMaitrise: f.coordonneesMaitrise ?? operation.coordonnees_maitrise ?? '',
    coordonneesMaitriseDOuvrage: f.coordonneesMaitriseDOuvrage ?? operation.coordonnees_maitrise_ouvrage ?? '',
    dateDebutOperation: f.dateDebutOperation ?? operation.date_debut_operation ?? '',
    dateFinOperation: f.dateFinOperation ?? operation.date_fin_operation ?? '',
    // Descriptif administratif (refonte PDF) — champs editables : front seul pour l'instant, defaut
    // chaine vide. Au branchement backend, ajouter le fallback colonne (?? operation.<colonne>) ici.
    typeCommande: f.typeCommande ?? 'Initiale', // derive (read-only) ; defaut en attendant la valeur backend
    pays: f.pays ?? operation.pays ?? '',
    phases: f.phases ?? operation.phases ?? '',
    moeInterlocuteur: f.moeInterlocuteur ?? operation.moe_interlocuteur ?? '',
    moeMail: f.moeMail ?? operation.moe_mail ?? '',
    moeTelephone: f.moeTelephone ?? operation.moe_telephone ?? '',
    signataire: f.signataire ?? operation.signataire_commande ?? '',
    modaliteFacturation: f.modaliteFacturation ?? operation.modalite_facturation ?? '',
    // Defaut "Public (P)" : toute operation sans valeur pre-selectionne Public (P) dans le select
    // (aligne sur le DEFAULT colonne cote back + le backfill de l'existant).
    niveauSensibilite: f.niveauSensibilite ?? operation.niveau_sensibilite ?? 'Public (P)',
    visiteObligatoire: f.visiteObligatoire ?? operation.visite_obligatoire ?? '',
    verificationIdentites: f.verificationIdentites ?? operation.verification_identites ?? '',
    habilitation: f.habilitation ?? operation.habilitation ?? '',
    charteReglementation: f.charteReglementation ?? operation.charte_reglementation ?? '',
    clauseSuiviEnvironnemental: f.clauseSuiviEnvironnemental ?? operation.clause_suivi_environnemental ?? '',
    // Descriptif technique (champs documentaires, optionnels).
    urlDocTechnique: f.urlDocTechnique ?? operation.url_doc_technique ?? '',
    modaliteAccesDocumentation: f.modaliteAccesDocumentation ?? operation.modalite_acces_documentation ?? '',
    listePiecesDocumentation: f.listePiecesDocumentation ?? operation.liste_pieces_documentation ?? '',
    // Descriptif procedural (champs saisis). dateLimiteReceptionOffres : vide -> le composant affiche
    // la valeur deduite par defaut (modifiable).
    dateLimiteReceptionOffres: f.dateLimiteReceptionOffres ?? operation.date_limite_reception_offres ?? '',
    dateOuverturePlis: f.dateOuverturePlis ?? operation.date_ouverture_plis ?? '',
    dateNotification: f.dateNotification ?? operation.date_notification ?? '',
    budgetMaximum: f.budgetMaximum ?? operation.budget_maximum ?? '',
    origineFinancement: f.origineFinancement ?? operation.origine_financement ?? '',
    sections: f.sections ?? operation.sections ?? [],
  };
};
